import type { Tutorial } from '../../shared/types.js';

export const connectSlackTutorial: Tutorial = {
  name: 'connect-slack',
  title: 'Connect your Slack account',
  description:
    'Link your personal Slack account to receive agent notifications. Completions, errors, and input requests are delivered to your Slack DMs.',
  category: 'integrations',
  docPages: ['concepts/plugins', 'administration/organization-onboarding'],
  agenda: ['Open Slack notifications', 'Choose what notifies you', 'Connect your Slack account'],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'Connect Slack for notifications',
      body: 'Reflex can DM you in Slack when your agents finish, error, or need input. This tutorial links your personal Slack account from your settings and walks you through the connect itself. Slack must already be set up for your organization by an admin. If you only see a "not configured" notice, ask an admin to finish the org-level Slack setup first.',
      path: '/settings#slack-notifications',
      rootLevel: true,
    },
    {
      id: 'overview',
      kind: 'info',
      title: 'Slack Notifications',
      body: 'This is the Slack Notifications section of your settings. Everything that controls your personal agent DMs lives here; which account they go to and which events trigger them.',
      target: '#slack-notifications',
      placement: 'top',
    },
    {
      id: 'account-status',
      kind: 'info',
      title: 'Your Slack account',
      body: 'This block shows how Reflex will reach you. It first tries to **match your account email** to a Slack profile. Upon success, you will see "DMs will go to …". If connection fails, you will see a warning. Once linked you will see "Connected as …".',
      target: '[data-tutorial-id="slack-account-status"]',
      placement: 'top',
    },
    {
      id: 'events',
      kind: 'info',
      title: 'Choose what notifies you',
      body: "Select the event types that you wish to receive by toggling the corresponding triggers. Once you're happy with the configuration, hit **Send test notification** to confirm delivery, then **Save preferences**.",
      target: '[aria-label="Enable notifications"]',
      placement: 'left',
    },
    {
      id: 'connect',
      kind: 'action',
      trigger: 'click',
      title: 'Connect your Slack account',
      body: 'Now let\'s connect the account in live mode: click **Connect Slack Account**. After you approve access on Slack, you\'ll return here showing "Connected as …", and agent messages will route to that account.',
      target: '[data-testid="slack-connect-account"]',
      // If the user is already connected when they reach this step (they linked
      // Slack before launching the tour), the "Connect" button isn't rendered —
      // the "Connected as …" panel is. Auto-complete the step in that case so
      // the tour finishes cleanly instead of getting stranded on an action whose
      // target doesn't exist.
      advanceWhen: '[data-tutorial-id="slack-connected"]',
      placement: 'top',
    },
  ],
};
